// Lista reutilizable de logs de actividad: chips de filtro por tipo de proceso,
// lista cronológica paginada y badges de estado. La usan tanto la vista GLOBAL
// del admin (todos los tenants, con columna de tenant) como la vista del OWNER
// (su propio tenant). Se parametriza con `fetchPage` (de dónde salen los datos)
// y `showTenant` (si se muestra el nombre del tenant por fila).

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { AlertMessage, Badge, Icon, Spinner } from '../ui/components.js';
import type { ActivityLogEntry, ActivityProcessType, AdminAuditCategory } from '../types.js';

const PAGE_SIZE = 50;

/** Icono por tipo de proceso (la etiqueta se resuelve vía i18n). */
export const PROCESS_ICON: Record<ActivityProcessType, string> = {
  record_change: 'edit_note',
  record_deletion: 'delete',
  permission_audit: 'admin_panel_settings',
  renumber: 'format_list_numbered',
  ingestion: 'auto_awesome',
  sync: 'sync',
  deal_hunter: 'sell',
  support: 'support_agent',
  billing: 'payments',
  admin_audit: 'tune',
};

/** Etiqueta i18n de un tipo de proceso. */
function processLabel(t: TFunction, type: ActivityProcessType): string {
  return t(`activityLog.process.${type}`, { defaultValue: type });
}

const ALL_TYPES: ActivityProcessType[] = [
  'record_change',
  'record_deletion',
  'permission_audit',
  'renumber',
  'ingestion',
  'sync',
  'deal_hunter',
  'support',
  'billing',
  'admin_audit',
];

/** Icono por subcategoría de `admin_audit` (la etiqueta se resuelve vía i18n). */
export const CATEGORY_ICON: Record<AdminAuditCategory, string> = {
  record: 'album',
  taxonomy: 'category',
  location: 'place',
  wishlist: 'favorite',
  duplicate: 'content_copy',
  top: 'star',
  config: 'settings',
  curation: 'auto_fix_high',
  plan: 'workspace_premium',
  account: 'manage_accounts',
  support: 'support_agent',
  audio: 'graphic_eq',
};

/** Etiqueta i18n de una subcategoría de administración. */
function categoryLabel(t: TFunction, category: AdminAuditCategory): string {
  return t(`activityLog.category.${category}`, { defaultValue: category });
}

/** Subcategorías de admin_audit en el orden en que se muestran las burbujas. */
const ALL_CATEGORIES: AdminAuditCategory[] = [
  'record',
  'taxonomy',
  'location',
  'wishlist',
  'duplicate',
  'top',
  'config',
  'curation',
  'plan',
  'account',
  'support',
  'audio',
];

/** Respuesta de una página del log (misma forma en /logs y /admin/logs). */
export interface ActivityLogPage {
  items: ActivityLogEntry[];
  has_more: boolean;
}

/** Tono del badge según la acción/estado del evento. */
function actionTone(action: string | null): 'neutral' | 'success' | 'warn' | 'info' | 'danger' {
  const a = (action ?? '').toLowerCase();
  if (
    ['completed', 'synced', 'active', 'resolved', 'closed', 'create', 'seed', 'create_owner'].includes(a)
  )
    return 'success';
  if (
    ['error', 'failed', 'revoke', 'delete', 'cancelled', 'canceled', 'payment_failed', 'delete_owner'].includes(a)
  )
    return 'danger';
  if (['running', 'pending', 'paused', 'proposed', 'flagged', 'open', 'status_change'].includes(a))
    return 'warn';
  return 'info';
}

/** Formatea una fecha ISO a algo legible y compacto. */
function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Fila individual del log, con detalle expandible al hacer clic. */
function LogRow({ entry, showTenant }: { entry: ActivityLogEntry; showTenant: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const icon = PROCESS_ICON[entry.process_type];
  const label = processLabel(t, entry.process_type);
  const detailLines = (entry.detail ?? '').split('\n').map((l) => l.trim()).filter((l) => l !== '');
  const hasDetail = detailLines.length > 0 || !!entry.record_label || !!entry.actor;

  return (
    <div className="surface-card !p-space-sm flex flex-col gap-space-xs">
      <button
        type="button"
        className="flex items-start gap-space-sm text-left w-full"
        onClick={() => hasDetail && setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="w-9 h-9 shrink-0 rounded-lg bg-surface-container-high flex items-center justify-center text-primary">
          <Icon name={icon ?? 'article'} className="text-[18px]" />
        </span>
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          <div className="flex items-center justify-between gap-space-sm">
            <span className="font-badge-micro text-badge-micro text-on-surface-variant uppercase tracking-wider">
              {label}
            </span>
            {entry.action && <Badge tone={actionTone(entry.action)}>{entry.action}</Badge>}
          </div>
          <span className="font-body-sm text-body-sm text-on-surface leading-tight break-words">
            {entry.summary ?? '—'}
          </span>
          {/* Disco asociado (si lo hay). */}
          {entry.record_label && (
            <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant truncate">
              <Icon name="album" className="text-[14px]" />
              {entry.record_label}
            </span>
          )}
          <div className="flex items-center justify-between gap-space-sm mt-0.5">
            {showTenant ? (
              <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant truncate">
                <Icon name="library_music" className="text-[14px]" />
                {entry.tenant_name ?? entry.tenant_id}
              </span>
            ) : (
              <span />
            )}
            <span className="flex items-center gap-space-xs shrink-0">
              <span className="font-code-catalog text-body-sm text-on-surface-variant">{fmtDate(entry.occurred_at)}</span>
              {hasDetail && (
                <Icon
                  name="expand_more"
                  className={`text-on-surface-variant text-[18px] transition-transform ${open ? 'rotate-180' : ''}`}
                />
              )}
            </span>
          </div>
        </div>
      </button>

      {/* Detalle expandido. */}
      {open && hasDetail && (
        <div className="ml-11 flex flex-col gap-space-xs surface-rack-card !p-space-sm">
          {entry.actor && (
            <span className="flex items-center gap-space-xs font-body-sm text-body-sm text-on-surface-variant">
              <Icon name="person" className="text-[14px]" /> {entry.actor}
            </span>
          )}
          {detailLines.map((line, i) => (
            <span key={i} className="font-body-sm text-body-sm text-on-surface break-words">
              {line}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export function ActivityLogList({
  fetchPage,
  showTenant,
  reloadKey,
}: {
  /** Carga una página dado el tipo (o '' = todos), la subcategoría, límite y offset. */
  fetchPage: (params: {
    type: ActivityProcessType | '';
    category: AdminAuditCategory | '';
    limit: number;
    offset: number;
  }) => Promise<ActivityLogPage>;
  /** Muestra el nombre del tenant por fila (vista global del admin). */
  showTenant: boolean;
  /** Al cambiar, fuerza recarga desde cero (p. ej. el admin cambió de tenant). */
  reloadKey?: string;
}) {
  const { t } = useTranslation();
  const [type, setType] = useState<ActivityProcessType | ''>('');
  // Subcategoría (burbuja de 2º nivel), solo aplicable cuando type = admin_audit.
  const [category, setCategory] = useState<AdminAuditCategory | ''>('');
  const [items, setItems] = useState<ActivityLogEntry[]>([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (nextOffset: number, filterType: ActivityProcessType | '', filterCategory: AdminAuditCategory | '') => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchPage({ type: filterType, category: filterCategory, limit: PAGE_SIZE, offset: nextOffset });
        setItems((prev) => (nextOffset === 0 ? res.items : [...prev, ...res.items]));
        setHasMore(res.has_more);
        setOffset(nextOffset);
      } catch (err) {
        setError(err instanceof Error ? err.message : t('activityLog.errLoad'));
      } finally {
        setLoading(false);
      }
    },
    [fetchPage],
  );

  // Cambiar de tipo de proceso: al salir de "Administración", se limpia la
  // subcategoría para no arrastrar un filtro que ya no aplica.
  const selectType = useCallback((next: ActivityProcessType | '') => {
    setType(next);
    if (next !== 'admin_audit') setCategory('');
  }, []);

  // Carga inicial y recarga al cambiar el filtro de tipo, la subcategoría o el
  // reloadKey.
  useEffect(() => {
    void load(0, type, category);
    // Recarga intencional solo al cambiar filtro/subcategoría/reloadKey; `load`
    // es estable y añadirla a deps provocaría recargas en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, category, reloadKey]);

  return (
    <div className="flex flex-col gap-space-md">
      {/* Filtro por tipo de proceso (burbujas de 1er nivel). */}
      <div className="flex flex-wrap gap-space-xs">
        <button
          type="button"
          className={`px-3 h-9 rounded-lg font-body-sm text-body-sm border transition-colors ${
            type === ''
              ? 'bg-primary-container/20 text-primary border-primary-container/50'
              : 'bg-surface-rack text-on-surface-variant border-border-subtle'
          }`}
          onClick={() => selectType('')}
        >
          {t('activityLog.all')}
        </button>
        {ALL_TYPES.map((pt) => (
          <button
            key={pt}
            type="button"
            className={`px-3 h-9 rounded-lg font-body-sm text-body-sm border transition-colors inline-flex items-center gap-space-xs ${
              type === pt
                ? 'bg-primary-container/20 text-primary border-primary-container/50'
                : 'bg-surface-rack text-on-surface-variant border-border-subtle'
            }`}
            onClick={() => selectType(pt)}
          >
            <Icon name={PROCESS_ICON[pt]} className="text-[16px]" />
            {processLabel(t, pt)}
          </button>
        ))}
      </div>

      {/* Filtro por subcategoría (burbujas de 2º nivel): solo para
          "Administración", que agrupa muchas acciones distintas. */}
      {type === 'admin_audit' && (
        <div className="flex flex-wrap gap-space-xs pl-space-sm border-l-2 border-primary-container/40">
          <button
            type="button"
            className={`px-3 h-8 rounded-lg font-body-sm text-body-sm border transition-colors ${
              category === ''
                ? 'bg-primary-container/20 text-primary border-primary-container/50'
                : 'bg-surface-rack text-on-surface-variant border-border-subtle'
            }`}
            onClick={() => setCategory('')}
          >
            {t('activityLog.allCategories')}
          </button>
          {ALL_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={`px-3 h-8 rounded-lg font-body-sm text-body-sm border transition-colors inline-flex items-center gap-space-xs ${
                category === c
                  ? 'bg-primary-container/20 text-primary border-primary-container/50'
                  : 'bg-surface-rack text-on-surface-variant border-border-subtle'
              }`}
              onClick={() => setCategory(c)}
            >
              <Icon name={CATEGORY_ICON[c]} className="text-[15px]" />
              {categoryLabel(t, c)}
            </button>
          ))}
        </div>
      )}

      <AlertMessage message={error} />

      {items.length === 0 && !loading && !error && (
        <p className="surface-card text-center text-on-surface-variant font-body-sm text-body-sm py-space-lg">
          {t('activityLog.empty')}
        </p>
      )}

      <div className="flex flex-col gap-space-xs">
        {items.map((entry) => (
          <LogRow key={`${entry.process_type}:${entry.source_id}`} entry={entry} showTenant={showTenant} />
        ))}
      </div>

      {loading && <Spinner label={t('activityLog.loading')} />}

      {hasMore && !loading && (
        <button
          className="btn-secondary w-full"
          type="button"
          onClick={() => void load(offset + PAGE_SIZE, type, category)}
        >
          <Icon name="expand_more" className="text-[18px]" /> {t('activityLog.loadMore')}
        </button>
      )}
    </div>
  );
}
